/**
 * @fileoverview Parameter list editor for the report builder's Parameters accordion —
 *             add, edit, reorder, and remove runtime parameters on a report definition.
 * @module ui-admin/components/ParameterEditor
 *
 * Responsibilities: - Render the ordered list of ParameterRow cards + "+ Add
 *                     Parameter" action, and surface validation errors per row.
 * Dependencies:     - React, lucide-react, @firesquirrel/core-domain (types +
 *                     validator), ./ParameterRow, ./parameterValidators.
 * Architecture:     - Presentation-only, controlled (parameters + onChange). The
 *                     host persists via its local-first service — typically
 *                     saveReportParameters, which revalidates before writing.
 * Notes:            - "+ Add Parameter" seeds a sensible default (date param,
 *                     relative mode) and auto-derives a unique parameterId.
 */

import React from 'react';
import { Plus } from 'lucide-react';
import type { ReportParameter } from '@firesquirrel/core-domain';
import { ParameterRow } from './ParameterRow';
import { generateParameterId, parameterValidationMap } from './parameterValidators';

/**
 * @contract ParameterEditorProps
 * Purpose: Props for the parameter list editor.
 * Fields:
 * - parameters: controlled array of declared parameters.
 * - onChange: emit the patched array (host persists).
 * - lookupTypeOptions: available lookup categories for lookup-type params
 *   (host supplies from its local-first lookup service).
 */
export interface ParameterEditorProps {
  parameters: ReportParameter[];
  onChange: (parameters: ReportParameter[]) => void;
  lookupTypeOptions?: string[];
  /** Available report fields for per-parameter "Filter Field" binding. */
  fieldOptions?: Array<{ field: string; label: string }>;
}

/**
 * @component ParameterEditor
 * Purpose:     Add/edit/reorder/remove runtime parameters on a report definition.
 * Props:       ParameterEditorProps.
 * Behavior:    Controlled — every mutation emits a new parameters array. New
 *              parameters get an auto-derived unique parameterId and a sensible
 *              default (date + relative mode). Per-row validation errors are
 *              computed live via the shared core-domain validator.
 * Data Source: none (controlled).
 * Constraints: presentation-only; persistence is the host's responsibility.
 */
export function ParameterEditor({ parameters, onChange, lookupTypeOptions, fieldOptions }: ParameterEditorProps) {
  const errors = parameterValidationMap(parameters);

  const patchAt = (index: number, next: ReportParameter) => {
    onChange(parameters.map((p, i) => (i === index ? next : p)));
  };

  const removeAt = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index));
  };

  const moveAt = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= parameters.length) return;
    const next = [...parameters];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const addParameter = () => {
    const label = `Parameter ${parameters.length + 1}`;
    onChange([
      ...parameters,
      {
        parameterId: generateParameterId(label, parameters),
        label,
        type: 'date',
        mode: 'relative',
        required: false,
        defaultValue: 'previousMonth',
      },
    ]);
  };

  return (
    <div data-testid="parameter-editor" className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-gray-500">
          Parameters ({parameters.length})
        </span>
        <button
          type="button"
          data-testid="add-parameter"
          onClick={addParameter}
          className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          <Plus className="h-3 w-3" /> Add Parameter
        </button>
      </div>

      {parameters.length === 0 && (
        <div data-testid="parameter-editor-empty" className="rounded-md border border-dashed border-gray-300 p-3 text-center text-xs text-gray-400">
          No parameters. Add one to let users filter this report at run time.
        </div>
      )}

      {parameters.map((param, index) => (
        <ParameterRow
          key={param.parameterId}
          parameter={param}
          index={index}
          total={parameters.length}
          lookupTypeOptions={lookupTypeOptions}
          fieldOptions={fieldOptions}
          errors={errors.get(param.parameterId) ?? errors.get('*')}
          onChange={(next) => patchAt(index, next)}
          onRemove={() => removeAt(index)}
          onMove={(dir) => moveAt(index, dir)}
        />
      ))}
    </div>
  );
}
